import { useEffect, useRef, useState } from 'react';
import { useApp } from '../app-context';
import { api } from '../api/client';
import { Dialog, ErrorAlert } from '../components/ui';
import { usePoll } from '../hooks/usePoll';
import { searchAll } from './index';
import { SearchBox } from './SearchBox';

function Palette({ onClose }: { onClose: () => void }) {
  const { me } = useApp();
  const [query, setQuery] = useState('');
  const servers = usePoll(api.servers, 30000);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let mounted = true;
    queueMicrotask(() => {
      if (mounted) ref.current?.querySelector('input')?.focus();
    });
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const dialog = ref.current?.closest('[role="dialog"]');
      const controls = dialog?.querySelectorAll<HTMLElement>('button, input');
      const first = controls?.[0];
      const last = controls?.[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    };
    document.addEventListener('keydown', trap);
    return () => {
      mounted = false;
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', trap);
    };
  }, []);
  return (
    <Dialog title="画面・設定を検索" onClose={onClose} actions={null} wide>
      <div ref={ref}>
        <SearchBox
          alwaysOpen
          query={query}
          onQuery={setQuery}
          onSelect={onClose}
          onEscape={onClose}
          results={searchAll(servers.data ?? [], me?.user?.role, query)}
        />
        {servers.loading && !servers.data && <p role="status">サーバーを読み込み中…</p>}
        <ErrorAlert error={servers.error} />
      </div>
    </Dialog>
  );
}

export function GlobalSettingsSearch() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      const editing =
        target instanceof HTMLElement &&
        Boolean(target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])'));
      if (event.isComposing || event.repeat || document.querySelector('dialog[open], [role="dialog"]')) return;
      const command = (event.ctrlKey || event.metaKey) && !event.altKey && event.key.toLowerCase() === 'k';
      const slash = event.key === '/' && !event.ctrlKey && !event.metaKey && !event.altKey && !editing;
      if (command || slash) {
        event.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <button
        className="btn small global-search-button"
        aria-label="画面・設定を検索"
        title="画面・設定を検索（Ctrl / ⌘ K）"
        onClick={() => setOpen(true)}
      >
        <span aria-hidden="true">⌕</span> 検索 <span className="small muted global-search-key">Ctrl K</span>
      </button>
      {open && <Palette onClose={() => setOpen(false)} />}
    </>
  );
}
